/**
 * The font key and the fallback walk shared by the asset registry
 * (fontRegistry.ts) and the metrics registry (fontMetricsRegistry.ts).
 *
 * Kept out of fontRegistry.ts, which globs the bundled atlases when it loads:
 * Vite builds that glob to `new URL(..., import.meta.url)` calls. Measurement
 * needs only the walk, so the metrics registry takes it from here, and the
 * authoring collector (`build/spatial`), which measures callout text, still
 * loads in a host with no URL (the gateway's isolate).
 */
import type { FontFamily, FontWeight, FontStyle } from '../../scenedata/behaviors/textdata';

export interface FontKey {
    family: FontFamily;
    weight: FontWeight;
    style: FontStyle;
}

export const DEFAULT_FAMILY: FontFamily = 'Roboto';
export const DEFAULT_WEIGHT: FontWeight = 'regular';
export const DEFAULT_STYLE: FontStyle = 'normal';

export function keyOf(k: FontKey): string {
    return `${k.family}|${k.weight}|${k.style}`;
}

/**
 * Pure resolver shared by the asset registry (fontRegistry.ts) and the
 * metrics registry (fontMetricsRegistry.ts), so both asset URLs and
 * measurement metrics resolve through ONE fallback chain and can never pick
 * different faces for the same request. The fallback chain (CSS-style) is:
 *
 *   1. Exact `(family, weight, style)`
 *   2. Drop `style` → `(family, weight, normal)`
 *   3. Drop `weight` → `(family, regular, normal)`
 *   4. Drop `family` → `(Roboto, weight, style)` — weight and style kept
 *   5. Then drop `style` → `(Roboto, weight, normal)`
 *   6. Then drop `weight` → `(Roboto, regular, normal)` — the default
 *
 * Steps 4-5 are the Text spec's conformance requirement 2, "Unknown values
 * fall back to the default family at the requested weight/style"
 * (`scenedata/behaviors/textdata.ts`). Without them a request for an
 * unavailable family drew Roboto Regular whatever weight was asked for, so
 * "bold" silently became "not bold" — a different sentence, not just a
 * different typeface — while Roboto's bold, italic and bold-italic faces all
 * ship.
 *
 * Steps 2-3 still run first, so an AVAILABLE family keeps the paragraph
 * inside itself: `Oswald italic` resolves to Oswald Regular, never to Roboto
 * Italic. The only requests steps 4-5 change are for a family with no face at
 * all, which before `fontFamily` opened the closed enum could not express.
 *
 * `onFallback` is invoked at most once with the requested key + the
 * fallback chosen, so callers can log substitutions.
 */
export function resolveAgainst<T>(
    registry: ReadonlyMap<string, T>,
    requested: FontKey,
    onFallback?: (requestedKey: string, fallback: FontKey) => void,
): T {
    const requestedKey = keyOf(requested);
    const exact = registry.get(requestedKey);
    if (exact) return exact;

    const fallbacks: FontKey[] = [
        { family: requested.family, weight: requested.weight, style: DEFAULT_STYLE },
        { family: requested.family, weight: DEFAULT_WEIGHT, style: DEFAULT_STYLE },
        { family: DEFAULT_FAMILY, weight: requested.weight, style: requested.style },
        { family: DEFAULT_FAMILY, weight: requested.weight, style: DEFAULT_STYLE },
        { family: DEFAULT_FAMILY, weight: DEFAULT_WEIGHT, style: DEFAULT_STYLE },
    ];

    for (const candidate of fallbacks) {
        const hit = registry.get(keyOf(candidate));
        if (hit) {
            onFallback?.(requestedKey, candidate);
            return hit;
        }
    }

    throw new Error(
        `[Text] No fallback available for "${requested.family} ${requested.weight} ${requested.style}"; ` +
        `the default "${DEFAULT_FAMILY} ${DEFAULT_WEIGHT} ${DEFAULT_STYLE}" must be bundled.`,
    );
}
